import { useState, useEffect } from "react";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiPlusCircle,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { useCommercialArchitectureDesign } from "../useHook";
import Swal from "sweetalert2";
import SimilarItemsModal from "../../../../Components/Modals/SimilarItems/SimilarItems";
import RecordPosition from "../../../../Components/Modals/RecordPosition/RecordPosition";
import { useNavigate, useSearchParams } from "react-router-dom";
import GlobalPagination from "../../../../Components/Pagination/GlobalPagination";
import { IoEye } from "react-icons/io5";
import { MdDelete } from "react-icons/md";
import {
  usePermission,
  SystemPermissions,
} from "../../../../Utils/permissionAccess";

const CommercialArchitectureDesignList = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(
    SystemPermissions.CommercialArchitectureDesign.CREATE,
  );
  const canUpdate = hasPermission(
    SystemPermissions.CommercialArchitectureDesign.UPDATE,
  );
  const canDelete = hasPermission(
    SystemPermissions.CommercialArchitectureDesign.DELETE,
  );
  const canSimilarDesign = hasPermission(
    SystemPermissions.CommercialArchitectureDesign.ADD_SIMILAR_DESIGN,
  );
  const canView = hasPermission(
    SystemPermissions.CommercialArchitectureDesign.VIEW,
  );
  const canPosition = hasPermission(
    SystemPermissions.CommercialArchitectureDesign.POSITION,
  );
  const showActions = canSimilarDesign || canView || canDelete || canUpdate;

  const navigate = useNavigate();
  const {
    getCommercialArchitectureDesign,
    deleteCommercialArchitectureDesign,
    searchCommercialArchitectureDesign,
    updateCommercialArchitectureDesignPosition,
  } = useCommercialArchitectureDesign();

  const [CommercialArchitectureDesignData, setCommercialArchitectureDesignData] =
    useState([]);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [selectedDesignForSimilar, setSelectedDesignForSimilar] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const pageParam = parseInt(searchParams.get("page") || "0", 10);
  const offset = pageParam * limit;
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedDesignForPosition, setSelectedDesignForPosition] =
    useState(null);

  useEffect(() => {
    loadCommercialArchitectureDesigns(offset, limit);
  }, [offset]);

  const handlePageChange = (newOffset) => {
    const newPage = Math.floor(newOffset / limit);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      return next;
    });
  };

  const closeSimilarModal = () => {
    setIsSimilarModalOpen(false);
    setSelectedDesignForSimilar(null);
  };

  const loadCommercialArchitectureDesigns = async (
    currentOffset = offset,
    currentLimit = limit,
    currentSearchFilter = searchFilter,
  ) => {
    setIsLoading(true);
    try {
      await getCommercialArchitectureDesign(
        setCommercialArchitectureDesignData,
        setTotal,
        currentOffset,
        currentLimit,
        currentSearchFilter,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load Commercial Architecture designs. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "0");
        return next;
      });
      const response = await searchCommercialArchitectureDesign(
        searchFilter,
        0,
        limit,
      );
      const designs = Array.isArray(response?.data) ? response.data : [];
      setCommercialArchitectureDesignData(designs);
      const totalRecords =
        response?.pagination?.totalRecords ||
        response?.totalCount ||
        designs.length;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setSearchParams({ page: "0" });
    loadCommercialArchitectureDesigns(0, limit, "");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteCommercialArchitectureDesign(id);
        if (response && response.success === false) {
          throw new Error(
            response.message || "Failed to delete Commercial Architecture design.",
          );
        }
        await loadCommercialArchitectureDesigns(offset, limit);
        Swal.fire(
          "Deleted!",
          response?.message ||
            "Commercial Architecture design deleted successfully.",
          "success",
        );
      } catch (error) {
        console.error("Failed to delete Commercial Architecture design:", error);
        Swal.fire(
          "Error!",
          error?.message ||
            "Failed to delete Commercial Architecture design. Please try again.",
          "error",
        );
      }
    }
  };

  const handlePageView = (id) => {
    navigate(`/commercial-architecture-design/${id}`);
  };

  const handleAddSimilarDesign = (id) => {
    setSelectedDesignForSimilar(id);
    setIsSimilarModalOpen(true);
  };

  const handlePositionClick = (design) => {
    setSelectedDesignForPosition({
      recordId: design.id,
      currentPosition: design.position ?? 0,
    });
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (recordId, newPosition) => {
    await updateCommercialArchitectureDesignPosition(recordId, newPosition);
    await loadCommercialArchitectureDesigns(offset, limit, searchFilter);
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedDesignForPosition(null);
  };

  return (
    <div>
      <div className="">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <h3 className="text-xl font-bold">Commercial Architecture Design</h3>

          <div className="flex flex-wrap gap-2">
            {canCreate && (
              <button
                onClick={() =>
                  navigate("/commercial-architecture-design/create")
                }
                className="btn btn-primary h-[44px]"
              >
                <FiPlus className="text-white" />
                Add Commercial Architecture Design
              </button>
            )}
          </div>
        </div>

        <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap md:justify-end items-center gap-3 my-5">
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search Commercial Architecture designs"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
            <FiSearch className="search-icon" />
          </div>
          <div className="flex flex-wrap md:flex-nowrap gap-2">
            <button
              className="btn btn-filter filter-btn"
              onClick={handleSearch}
            >
              <FiFilter className="text-white" /> Apply Filter
            </button>
            <button
              className="btn btn-reset-filter filter-btn"
              onClick={handleReset}
            >
              <FiRotateCcw className="text-white" /> Reset Filter
            </button>
          </div>
        </div>

        <div className="bg-white w-full rounded-lg pagination-shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
              <thead>
                <tr className="brand-gradient text-white">
                  <th className="table-th">Sr. No.</th>
                  <th className="table-th">Title</th>
                  <th className="table-th">Plot Dimension</th>
                  <th className="table-th">Covered Area</th>
                  {showActions && <th className="table-th">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {CommercialArchitectureDesignData.length > 0 ? (
                  CommercialArchitectureDesignData.map((design, index) => (
                    <tr
                      key={design.id}
                      className={`${
                        index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                      } hover:bg-gray-200 transition-colors duration-300`}
                    >
                      <td className="table-td">
                        {canPosition ? (
                          <button
                            type="button"
                            onClick={() => handlePositionClick(design)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                          >
                            {design.position ?? "—"}
                          </button>
                        ) : (
                          <span className="font-medium text-gray-500">
                            {design.position ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="table-td">
                        {design.title || design.name}
                      </td>
                      <td className="table-td">{design.plot_dimension}</td>
                      <td className="table-td">{design.covered_area}</td>
                      {showActions && (
                        <td className="table-td">
                          <div className="flex space-x-2">
                            {canSimilarDesign && (
                              <button
                                className="btn btn-action"
                                onClick={() =>
                                  handleAddSimilarDesign(design.id)
                                }
                              >
                                <FiPlusCircle className="text-white" />
                                Similar Items
                              </button>
                            )}
                            {canUpdate && (
                              <button
                                className="btn btn-edit text-white"
                                onClick={() =>
                                  navigate(
                                    `/commercial-architecture-design/edit/${design.id}`,
                                  )
                                }
                              >
                                <FiEdit2 className="text-white" /> Edit
                              </button>
                            )}
                            {canView && (
                              <button
                                className="btn btn-view"
                                onClick={() => handlePageView(design.id)}
                              >
                                <IoEye className="text-white" /> View
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className="btn btn-delete text-white"
                                onClick={() => handleDelete(design.id)}
                              >
                                <MdDelete className="text-white" /> Delete
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={showActions ? 5 : 4}
                      className="py-4 px-4 text-center text-gray-500 font-bold"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          Loading Commercial Architecture designs...
                        </div>
                      ) : (
                        "No Commercial Architecture designs found"
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {CommercialArchitectureDesignData.length > 0 && total > limit && (
          <div className="mt-4">
            <GlobalPagination
              offset={offset}
              limit={limit}
              total={total}
              onPageChange={handlePageChange}
              className="bg-white"
            />
          </div>
        )}
      </div>

      {isSimilarModalOpen && (
        <SimilarItemsModal
          isOpen={isSimilarModalOpen}
          onClose={closeSimilarModal}
          sourceId={selectedDesignForSimilar}
          sourceType="commercial_architecture_design"
        />
      )}

      {isPositionModalOpen && selectedDesignForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedDesignForPosition.currentPosition}
          recordId={selectedDesignForPosition.recordId}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Commercial Architecture Design"
        />
      )}
    </div>
  );
};

export default CommercialArchitectureDesignList;
